import { create } from 'zustand';
import dayjs from 'dayjs';
import axios from 'axios';
import { get_records, rollback_renovation_customer } from '../utils/apis/records';
import { get_customers } from '../utils/apis/customers';
import { get_renovation_nexttendays, send_email_report } from '../utils/apis/home';
import { notifications } from '@mantine/notifications';

export const useDashboardStore = create((set, get) => ({
  itemsCustomers: null,
  itemsPostpago: null,
  itemsPrepago: null,
  totalPostpago: null,
  totalPrepago: null,
  totalAmount: null,
  records: null,
  renovation: null,
  cronLogs: [],
  banks: null,
  loadingBanks: true,
  rollbackData: null,
  openRollBack: false,
  sendingEmail: false,
  momStats: null, // New field for Month-over-Month stats

  sendEmailReport: async () => {
    set({ sendingEmail: true });
    try {
      const res = await send_email_report();
      if (res?.status) {
        notifications.show({
          title: 'Reporte enviado',
          message: 'El reporte de cuentas se ha enviado correctamente por correo.',
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Error',
          message: res?.message || 'No se pudo enviar el reporte.',
          color: 'red',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error de conexión',
        message: 'Ocurrió un error al intentar enviar el reporte.',
        color: 'red',
      });
    } finally {
      set({ sendingEmail: false });
    }
  },

  fetchDashboardData: async () => {
    try {
      const startDate = dayjs().startOf('month').format('YYYY-MM-DD');
      const endDate = dayjs().endOf('month').add(1, 'day').format('YYYY-MM-DD');

      const [recordsRes, customersRes, renovationRes, cronLogsRes] = await Promise.all([
        get_records({
          filter: [dayjs(startDate).format('DD/MM/YYYY'), dayjs(endDate).format('DD/MM/YYYY')],
          status: true
        }),
        get_customers(),
        get_renovation_nexttendays(),
        axios.get(`${process.env.REACT_APP_API_URL}/monitor/cron-logs`).then(res => res.data).catch(() => [])
      ]);
      
      const records = recordsRes?.records || [];
      const totalAmount = recordsRes?.total_amount || 0;
      const typeContracts = recordsRes?.type_contracts || [];
      const totalPostpago = typeContracts.find(c => c.name === 'Postpago')?.amount || 0;
      const totalPrepago = typeContracts.find(c => c.name === 'Prepago')?.amount || 0;
      const momStats = recordsRes?.momStats || null;

      const customers = Array.isArray(customersRes) ? customersRes : [];
      const renovations = Array.isArray(renovationRes) ? renovationRes : [];

      const backendBanks = recordsRes?.type_banks || [];
      const predefinedBanks = [
        "Banregio", "Azteca", "BBVA", "Hsbc", "Mercado Pago", "Santander", "Efectivo", "Spin",
      ];

      const banks = predefinedBanks.map(bankName => {
        const found = backendBanks.find(b => b.name?.toLowerCase() === bankName.toLowerCase());
        return {
          bank: bankName,
          amount: found ? found.amount : 0
        };
      });

      set({
        itemsCustomers: customers.length,
        itemsPostpago: customers.filter((item) => item.type_contract === 'Postpago').length,
        itemsPrepago: customers.filter((item) => item.type_contract === 'Prepago').length,
        totalPostpago: totalPostpago,
        totalPrepago: totalPrepago,
        totalAmount: totalAmount,
        momStats: momStats,
        records: records.slice(0, 9),
        renovation: renovations
          .filter((item) => dayjs(item.limit_date_billing).isAfter(dayjs().subtract(1, 'day')))
          .sort((a, b) => dayjs(a.limit_date_billing).diff(dayjs(b.limit_date_billing)))
          .slice(0, 9),
        banks: banks,
        cronLogs: Array.isArray(cronLogsRes) ? cronLogsRes.slice(0, 10) : [],
        loadingBanks: false,
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      set({ loadingBanks: false });
    }
  },

  toggleRollback: (item) => set({ openRollBack: true, rollbackData: item }),

  closeRollback: () => set({ openRollBack: false, rollbackData: null }),

  rollBackRenovation: async (item) => {
    try {
      await rollback_renovation_customer({ _id: item._id });
      set({ openRollBack: false, rollbackData: null });
      await get().fetchDashboardData();
    } catch (error) {
      console.error("Error in rollback:", error);
    }
  }
}));
